/**
 * Shared attachment preview utilities and components.
 * Used by RequestDetail (Graph API attachments) and QueuePage (blob attachments).
 */
import React, { useRef, useState } from "react";

export type PreviewData = {
  url: string; contentType: string; filename: string;
  htmlContent?: string;
  tableHtml?: string;
  textContent?: string;
};

export function resolveContentType(ct: string, name: string): string {
  if (ct && ct !== "application/octet-stream" && ct !== "application/force-download") return ct;
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  const map: Record<string, string> = {
    pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg",
    gif: "image/gif", webp: "image/webp", bmp: "image/bmp", svg: "image/svg+xml",
    tif: "image/tiff", tiff: "image/tiff",
    doc: "application/msword", docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    csv: "text/csv", txt: "text/plain", log: "text/plain", md: "text/markdown",
    json: "application/json", xml: "application/xml", yml: "text/yaml", yaml: "text/yaml",
    html: "text/html", htm: "text/html",
    mp4: "video/mp4", mp3: "audio/mpeg", wav: "audio/wav",
    eml: "message/rfc822", msg: "application/vnd.ms-outlook",
  };
  return map[ext] ?? ct;
}

export function fileIcon(ct: string, name: string): string {
  if (ct.startsWith("image/")) return "image";
  if (ct.startsWith("video/")) return "movie";
  if (ct.startsWith("audio/")) return "audio_file";
  if (ct === "application/pdf") return "picture_as_pdf";
  if (ct.includes("spreadsheet") || ct.includes("excel") || name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv")) return "table_chart";
  if (ct.includes("word") || ct.includes("document") || name.endsWith(".docx") || name.endsWith(".doc")) return "description";
  if (ct.startsWith("text/") || name.endsWith(".json") || name.endsWith(".xml") || name.endsWith(".txt") || name.endsWith(".log")) return "article";
  return "attach_file";
}

export function isTextType(ct: string, name: string): boolean {
  return ct.startsWith("text/") || ct === "application/json" || ct === "application/xml"
    || /\.(txt|log|csv|json|xml|md|yml|yaml)$/i.test(name);
}

export function isDocx(ct: string, name: string): boolean {
  return ct.includes("wordprocessingml") || ct.includes("msword") || /\.docx?$/i.test(name);
}

export function isXlsx(ct: string, name: string): boolean {
  return ct.includes("spreadsheetml") || ct.includes("ms-excel") || /\.xlsx?$/i.test(name) || /\.csv$/i.test(name);
}

export async function renderDocx(blob: Blob): Promise<string> {
  const mammoth = await import("mammoth");
  const arrayBuffer = await blob.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer });
  return result.value;
}

export async function renderXlsx(blob: Blob): Promise<string> {
  const XLSX = await import("xlsx");
  const arrayBuffer = await blob.arrayBuffer();
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const sheets: string[] = [];
  for (const name of wb.SheetNames) {
    const html = XLSX.utils.sheet_to_html(wb.Sheets[name], { editable: false });
    sheets.push(`<div style="margin-bottom:16px"><strong>${name}</strong>${html}</div>`);
  }
  return sheets.join("");
}

/** Build PreviewData from a blob URL, resolving rich content (DOCX, XLSX, text). */
export async function buildPreviewData(
  url: string, rawCT: string, filename: string,
): Promise<{ preview: PreviewData; renderError?: string }> {
  const contentType = resolveContentType(rawCT, filename);
  const preview: PreviewData = { url, contentType, filename };
  let renderError: string | undefined;

  if (isDocx(contentType, filename)) {
    try {
      const resp = await fetch(url);
      preview.htmlContent = await renderDocx(await resp.blob());
    } catch { renderError = "Anteprima DOCX non disponibile"; }
  } else if (isXlsx(contentType, filename)) {
    try {
      const resp = await fetch(url);
      preview.tableHtml = await renderXlsx(await resp.blob());
    } catch { renderError = "Anteprima Excel non disponibile"; }
  } else if (isTextType(contentType, filename) || contentType === "message/rfc822") {
    try {
      const resp = await fetch(url);
      preview.textContent = await resp.text();
    } catch { renderError = "Anteprima testo non disponibile"; }
  }

  return { preview, renderError };
}

/** Renders the content of a preview (image, PDF, video, text, etc.). */
export function AttachmentPreviewContent({ data }: { data: PreviewData }) {
  const { url, contentType, filename, htmlContent, tableHtml, textContent } = data;

  if (contentType.startsWith("image/"))
    return <img src={url} alt={filename} style={{ maxWidth: "100%", maxHeight: "80vh", display: "block", margin: "0 auto" }} />;

  if (contentType === "application/pdf")
    return <iframe src={url} title={filename} style={{ width: "100%", height: "80vh", border: "none" }} />;

  if (contentType.startsWith("video/"))
    return <video src={url} controls style={{ maxWidth: "100%", maxHeight: "80vh", display: "block", margin: "0 auto" }} />;

  if (contentType.startsWith("audio/"))
    return <div style={{ padding: "var(--space-6)", textAlign: "center" }}><audio src={url} controls style={{ width: "100%" }} /></div>;

  if (htmlContent)
    return <div style={{ padding: "var(--space-4)", lineHeight: 1.6, fontSize: "var(--font-size-sm)" }} dangerouslySetInnerHTML={{ __html: htmlContent }} />;

  if (tableHtml)
    return <div style={{ padding: "var(--space-2)", fontSize: "var(--font-size-sm)", overflow: "auto" }} dangerouslySetInnerHTML={{ __html: tableHtml }} />;

  if (textContent != null) {
    const isBinary = /[\x00-\x08\x0E-\x1F]/.test(textContent.slice(0, 512));
    const isMimeBase64 = /Content-Transfer-Encoding:\s*base64/i.test(textContent.slice(0, 1000));

    if (isBinary || isMimeBase64) {
      if (isMimeBase64) {
        const isHtml = /Content-Type:\s*text\/html/i.test(textContent.slice(0, 500));
        const parts = textContent.split(/\r?\n\r?\n/);
        const b64Body = parts.slice(1).join("").replace(/\s/g, "");
        if (b64Body.length > 20) {
          try {
            const decoded = atob(b64Body);
            const bytes = Uint8Array.from(decoded, c => c.charCodeAt(0));
            const text = new TextDecoder("utf-8").decode(bytes);
            if (isHtml) {
              return <div style={{ padding: "var(--space-4)", lineHeight: 1.6, fontSize: "var(--font-size-sm)", maxHeight: "80vh", overflow: "auto" }}
                dangerouslySetInnerHTML={{ __html: text }} />;
            }
            return <pre style={preStyle}>{text}</pre>;
          } catch { /* decode failed */ }
        }
      }
      return <FallbackDownload url={url} filename={filename} message="File binario — anteprima non disponibile." />;
    }
    return <pre style={preStyle}>{textContent}</pre>;
  }

  return <FallbackDownload url={url} filename={filename} message="Anteprima non disponibile per questo formato." />;
}

function FallbackDownload({ url, filename, message }: { url: string; filename: string; message: string }) {
  return (
    <div style={{ padding: "var(--space-6)", textAlign: "center" }}>
      <span className="material-symbols-outlined" style={{ fontSize: 48, color: "var(--indaco-gray-400)", display: "block", marginBottom: "var(--space-3)" }}>file_present</span>
      <p className="text-muted">{message}</p>
      <a href={url} download={filename} className="btn btn-outline btn-sm" style={{ marginTop: "var(--space-3)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>download</span> Scarica file
      </a>
    </div>
  );
}

/** Full preview modal with header, download button, close. */
export function PreviewModal({ data, renderError, onClose }: {
  data: PreviewData; renderError?: string | null; onClose: () => void;
}) {
  const backdropMouseDown = useRef(false);
  return (
    <div className="dialog-overlay"
      onMouseDown={(e) => { backdropMouseDown.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (e.target === e.currentTarget && backdropMouseDown.current) onClose(); }}>
      <div className="dialog-card" style={{
        maxWidth: 900, width: "min(90vw, 900px)", minWidth: 360,
        display: "flex", flexDirection: "column", gap: 10,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>{data.filename}</span>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <a href={data.url} download={data.filename} className="btn btn-outline btn-sm">
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>download</span> Scarica
            </a>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
            </button>
          </div>
        </div>
        {renderError && <div style={{ fontSize: 12, color: "var(--indaco-warning)" }}>{renderError}</div>}
        <div style={{ overflow: "auto", flex: 1, maxHeight: "80vh" }}>
          <AttachmentPreviewContent data={data} />
        </div>
      </div>
    </div>
  );
}

/** Hook-style helper: manages preview state + modal for any component. */
export function useAttachmentPreview() {
  const [previewing, setPreviewing] = useState<PreviewData | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function openPreview(fetchUrl: () => Promise<{ url: string; contentType: string }>, filename: string) {
    setError(null);
    setRenderError(null);
    setLoading(filename);
    try {
      const { url, contentType: rawCT } = await fetchUrl();
      const { preview, renderError: re } = await buildPreviewData(url, rawCT, filename);
      if (re) setRenderError(re);
      setPreviewing(preview);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Errore download";
      if (msg.includes("404")) setError("Allegato non disponibile");
      else setError(msg);
    } finally {
      setLoading(null);
    }
  }

  function closePreview() {
    if (previewing) URL.revokeObjectURL(previewing.url);
    setPreviewing(null);
    setRenderError(null);
  }

  return { previewing, renderError, loading, error, openPreview, closePreview };
}

const preStyle: React.CSSProperties = {
  padding: "var(--space-4)", background: "var(--indaco-gray-50)", borderRadius: "var(--border-radius-md)",
  fontSize: "var(--font-size-sm)", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "80vh", overflow: "auto",
};
